import { useCallback } from 'react';
import { useReservationsContext } from '../context/ReservationsContext';

/**
 * Estados efectivos para renderizado en UI
 * Prioridad: HIDDEN > SOLD > RESERVED > AVAILABLE
 */
export const EffectiveStatus = {
    AVAILABLE: 'available',
    RESERVED: 'reserved',
    SOLD: 'sold',
    HIDDEN: 'hidden'
};

/**
 * Hook para calcular el estado efectivo de un producto
 * Lee el status de la coleccion 'reservations' en Firestore
 *
 * Jerarquia de prioridad:
 * 1. HIDDEN - producto no se muestra
 * 2. SOLD - producto vendido (desactivado completamente)
 * 3. RESERVED - reserva activa de 48h
 * 4. AVAILABLE - disponible para reservar
 */
export const useProductStatus = () => {
    const { isProductReserved, getTimeRemaining, getProductStatus } = useReservationsContext();

    /**
     * Obtiene el estado efectivo de un producto
     * @param {Object} product - Producto con campo id
     * @returns {Object} { effectiveStatus, timeRemaining, canReserve, isHidden }
     */
    const getEffectiveStatus = useCallback((product) => {
        if (!product) {
            return {
                effectiveStatus: EffectiveStatus.AVAILABLE,
                timeRemaining: null,
                canReserve: false,
                isHidden: false
            };
        }

        // Obtener status de la coleccion reservations
        const status = getProductStatus(product.id);

        // Prioridad 1: HIDDEN - no mostrar
        if (status === 'hidden') {
            return {
                effectiveStatus: EffectiveStatus.HIDDEN,
                timeRemaining: null,
                canReserve: false,
                isHidden: true
            };
        }

        // Prioridad 2: SOLD siempre gana
        if (status === 'sold') {
            return {
                effectiveStatus: EffectiveStatus.SOLD,
                timeRemaining: null,
                canReserve: false,
                isHidden: false
            };
        }

        // Prioridad 3: Verificar reserva activa (contador 48h)
        if (isProductReserved(product.id)) {
            return {
                effectiveStatus: EffectiveStatus.RESERVED,
                timeRemaining: getTimeRemaining(product.id),
                canReserve: false,
                isHidden: false
            };
        }

        // Prioridad 4: Disponible
        return {
            effectiveStatus: EffectiveStatus.AVAILABLE,
            timeRemaining: null,
            canReserve: true,
            isHidden: false
        };
    }, [isProductReserved, getTimeRemaining, getProductStatus]);

    /**
     * Verifica si un producto puede ser reservado
     * @param {Object} product - Producto a verificar
     * @returns {boolean}
     */
    const canProductBeReserved = useCallback((product) => {
        if (!product) return false;
        const status = getProductStatus(product.id);
        if (status === 'sold' || status === 'hidden') return false;
        if (isProductReserved(product.id)) return false;
        return true;
    }, [isProductReserved, getProductStatus]);

    return {
        getEffectiveStatus,
        canProductBeReserved,
        EffectiveStatus
    };
};
